<template lang='pug'>
  .fpui-input-text(:class='themeClass', @keyup='onKeyUp')
    .row
      .fpui-input-label-container.vcenter(
        @click='focus',
        :class='labelClass',
        v-if='label'
      )
        span {{ label | translate }}
        .required-container(v-if='required')
          i.fpui.fpui-required
        fpui-helper(v-if='helper', :text="helper | translate")
      .fpui-input-text-content.vcenter(:class='inputClass')
        input(
          ref='input',
          :name='name',
          :placeholder="placeholder | translate",
          :type="type",
          :step="step"
          :id="id",
          v-model='model',
          :class="className",
          :disabled="disabled",
          @focus='onFocus',
          @focusout='onFocusOut',
          @click="$emit('click', $event)"
        )
        ul.autocomplete(
          v-if='autocomplete && inAutocomplete.length >0'
          :class='{ "autocomplete-top": autocompletePosition === "top", "hide-autocomplete": hideAutocomplete }'
        )
          li(
            v-for='(auto, index) in inAutocomplete'
            :class='{selected: auto.active}',
            @click="onAutocomplete(auto.value)"
          ) {{ auto.label || auto.value }}
        i.clear-input.fpui.fpui-remove(@click="model = ''", v-if='clearable && autocomplete && value.length >0 && !disabled')
</template>

<script>

export default {
  props: {
    id: {
      type: String,
      required: false,
      default () { return Math.random().toString(36).replace(/[^a-z]+/g, '').substr(2, 10) }
    },
    name: { type: String },
    value: { required: true, default: '' },
    color: { type: String, default: 'blue' },
    disabled: { type: Boolean, default: false },
    error: { type: Boolean, default: false },
    placeholder: { type: String },
    autocomplete: { type: Array },
    autocompletePosition: { type: String },
    type: { type: String, default: 'text' },
    step: { type: String, default: 'text' },
    autofocus: { type: Boolean, default: false },
    selectAllOnFocus: { type: Boolean, default: false },
    label: { type: String },
    labelSize: { type: Number, default: 3 },
    withPadding: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    helper: { type: String },
    clearable: { type: Boolean, default: true },
    theme: { type: String, default: 'v3' }
  },
  data () {
    return {
      inAutocomplete: [],
      focused: false,
      hideAutocomplete: true
    }
  },
  computed: {
    model: {
      get () { return this.value },
      set (val) {
        if (this.autocomplete) {
          this.updateAutocomplete(val)
        }
        this.$emit('input', val)
      }
    },
    className () {
      let className = [`fpui-input-color-${this.color}`]
      if (this.disabled) className.push('disabled')
      if (this.error) className.push('error')
      return className.join(' ')
    },
    inputClass () {
      let className = []
      let size = 12
      if (this.label) size -= this.labelSize
      className.push(`col-xs-${size}`)
      if (this.withPadding) className.push('with-padding')
      return className.join(' ')
    },
    labelClass () {
      let className = [`col-xs-${this.labelSize}`]
      if (this.withPadding) className.push('with-padding')
      return className.join(' ')
    },
    themeClass () {
      let className = [this.theme]
      if (this.focused) className.push('focused')
      if (this.error) className.push('error')
      return className.join(' ')
    }
  },
  mounted () {
    this.onKeyUp = this.onKeyUp.bind(this)
    if (this.autofocus) this.focus()
  },
  methods: {
    onFocusOut () {
      this.focused = false
    },
    onFocus (e) {
      this.focused = true
      if (this.selectAllOnFocus) {
        e.target.select()
      }
    },
    onAutocomplete (val) {
      this.model = val
      this.hideAutocomplete = true
    },
    onKeyUp (e) {
      if (this.inAutocomplete.length > 0) {
        let index = 0
        for (let i = 0; i < this.inAutocomplete.length; i++) {
          const auto = this.inAutocomplete[i]
          if (auto.active) { index = i }
        }
        // Enter key
        if (e.keyCode === 13) {
          this.onAutocomplete(this.inAutocomplete[index].value)
          return
        }
        // Down key
        if (e.keyCode === 40) {
          this.inAutocomplete[index].active = false
          index = index === (this.inAutocomplete.length - 1) ? 0 : index + 1
          this.inAutocomplete[index].active = true
        }
        // Up key
        if (e.keyCode === 38) {
          this.inAutocomplete[index].active = false
          index = index === 0 ? this.inAutocomplete.length - 1 : index - 1
          this.inAutocomplete[index].active = true
        }
      }
      this.$emit('keyup', e)
    },
    focus () { this.$refs.input && this.$refs.input.focus() },
    updateAutocomplete (val) {
      if (val && val.length > 0) {
        const tmp = this.autocomplete.filter(auto => {
          try {
            return new RegExp(val, 'i').test(auto)
          } catch (err) {
            return false
          }
        })
        this.inAutocomplete = tmp.map((auto, index) => {
          if (index === 0) { return {value: auto, active: true} }
          return {value: auto, active: false}
        })
        this.hideAutocomplete = false
      } else {
        this.inAutocomplete = []
        this.hideAutocomplete = true
      }
    }
  },
  watch: {
    autocomplete () {
      this.updateAutocomplete(this.model)
    }
  }
}
</script>

<style lang='less'>
@import "~@/shared/styles/_variables.less";
@import "~@/shared/styles/theme_v4/_variables.less";

// theme_v3
.fpui-input-text.v3 {
  position:relative;
  .fpui-input-label-container {
    padding-left: 0;
    cursor: pointer;
    .required-container {
      display: inline-block;
      margin-right: 7px;
      margin-top: -10px;
      width: 15px;
      vertical-align: middle;
      font-size: 22px;
      text-align: center;
      color: @blue;
      border-radius: 50%;
    }
  }
  .fpui-input-text-content:not(.with-padding) {
    padding: 0;
  }
  input {
    width: 100%;
    border-radius: 2px;
    background: #FFF none repeat scroll 0% 0%;
    padding: 8px;
    border: 1px solid @grey;
    font-size: 1em;
    font-weight: 300;
    letter-spacing: 1px;
    outline: none;
    &:disabled {
      background: #eee;
    }
    .input-transition();
    &.fpui-input-color-blue:not(.error):focus {
      border-color: @blue;
    }
    &.fpui-input-color-green:not(.error):focus {
      border-color: @green;
    }
    &.fpui-input-color-red:focus, &.error {
      border-color: @red;
    }
    &.fpui-input-color-yellow:not(.error):focus {
      border-color: @yellow;
    }
    &::placeholder, &:-ms-input-placeholder, &::-ms-input-placeholder {
      color: #9a9a9a;
    }
  }
  .autocomplete {
    padding: 0;
    margin: 0;
    list-style: none;
    position:absolute;
    left: 0;
    width: 100%;
    z-index: 1000;
    background-color: white;
    display: block;
    li {
      padding: 7px;
      cursor: pointer;
      &:hover, &.selected {
        background-color: @grey;
      }
    }
    &.autocomplete-top {
      bottom: 40px;
      overflow-y: scroll;
    }
    &.hide-autocomplete {
      display: none;
    }
  }
  .clear-input {
    position:absolute;
    right: 5px;
    top: 0px;
    color: @blue;
    cursor:pointer;
    font-size: 15px;
  }
}

// theme_v4
.fpui-input-text.v4 {
  position:relative;
  .fpui-input-label-container {
    padding-left: 0;
    cursor: pointer;
    .required-container {
      display: inline-block;
      margin-right: 7px;
      margin-top: -10px;
      width: 15px;
      vertical-align: middle;
      font-size: 22px;
      text-align: center;
      color: @blue_v4;
      border-radius: 50%;
    }
  }
  .fpui-input-text-content:not(.with-padding) {
    padding: 0;
  }
  input {
    width: 100%;
    border-radius: 2px;
    background: @background-color_v4 none repeat scroll 0% 0%;
    padding: 8px;
    border: 1px solid @grey_v4;
    font-size: 1em;
    font-weight: 300;
    letter-spacing: 1px;
    outline: none;
    &:disabled {
      background: #eee;
    }
    .input-transition();
    &.fpui-input-color-blue:not(.error):focus {
      border-color: @blue_v4;
    }
    &.fpui-input-color-green:not(.error):focus {
      border-color: @green_v4;
    }
    &.fpui-input-color-red:focus, &.error {
      border-color: @red;
    }
    &.fpui-input-color-yellow:not(.error):focus {
      border-color: @yellow;
    }
  }
  .autocomplete {
    padding: 0;
    margin: 0;
    list-style: none;
    position:absolute;
    left: 0;
    width: 100%;
    z-index: 1000;
    opacity: 1;
    background-color: white;
    display: block;
    li {
      padding: 7px;
      cursor: pointer;
      &:hover, &.selected {
        background-color: @grey_v4;
      }
    }
    &.autocomplete-top {
      bottom: 40px;
      overflow-y: scroll;
    }
    &.hide-autocomplete {
      display: none;
    }
  }
  .clear-input {
    position:absolute;
    right: 5px;
    top: 0px;
    color: @blue_v4;
    cursor:pointer;
    font-size: 15px;
  }
}
</style>
